import React from 'react';
import { reportError } from '../utils/crashReporter';

const RETRY_KEY = 'tapn_crash_autofix';
const isStaleBuild = (e) => /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Unexpected token '<'|__WEBPACK_DEFAULT_EXPORT__|is not defined/i.test(`${e?.name || ''} ${e?.message || ''}`);

// Clears stale service-worker caches and reloads once — fixes most "old build" crashes after a deploy.
export const autoFixStaleBuild = async () => {
  const tries = Number(sessionStorage.getItem(RETRY_KEY) || 0);
  if (tries >= 2) return false;
  sessionStorage.setItem(RETRY_KEY, String(tries + 1));
  try {
    if ('caches' in window) { const keys = await caches.keys(); await Promise.all(keys.map((k) => caches.delete(k))); }
    if (navigator.serviceWorker) { const regs = await navigator.serviceWorker.getRegistrations(); await Promise.all(regs.map((r) => r.update().catch(() => {}))); }
  } catch { /* best effort */ }
  window.location.reload();
  return true;
};

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retried: false, fixing: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    reportError(error?.message || 'React render crash', `${error?.stack || ''}\n${info?.componentStack || ''}`, 'react_boundary');
    if (isStaleBuild(error)) {
      this.setState({ fixing: true });
      autoFixStaleBuild().then((did) => { if (!did) this.setState({ fixing: false }); });
      return;
    }
    if (!this.state.retried) {
      // Transient render crash: retry once automatically before showing the fallback
      setTimeout(() => this.setState({ hasError: false, retried: true }), 800);
    }
  }

  componentDidUpdate(_, prev) {
    if (prev.hasError && !this.state.hasError) sessionStorage.removeItem(RETRY_KEY);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.fixing || !this.state.retried) {
        return (
          <div className="min-h-screen bg-[#0c1018] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="error-boundary-autofix">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-slate-300 text-sm">Refreshing to the latest version…</p>
          </div>
        );
      }
      return (
        <div className="min-h-screen bg-[#0c1018] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="error-boundary-fallback">
          <div className="text-5xl mb-4">😵</div>
          <h1 className="text-2xl font-black mb-2">Something went wrong</h1>
          <p className="text-slate-400 text-sm mb-6 max-w-md">We hit a snag loading this page. Our team was notified automatically.</p>
          <div className="flex gap-3">
            <button onClick={() => this.setState({ hasError: false })} className="rounded-full border border-white/20 text-white font-bold px-6 py-2.5" data-testid="error-boundary-retry">Try again</button>
            <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }} className="rounded-full bg-amber-500 text-slate-900 font-bold px-6 py-2.5" data-testid="error-boundary-reload">Back to home</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
